/**
 * Enhanced Alliance Management Dialog
 * Advanced features: member roles, permissions, alliance stats, alliance chat, and diplomacy
 */

import Phaser from 'phaser';
import { MMOStateManager } from '../../managers/MMOStateManager';
import { MMONetworkManager } from '../../managers/MMONetworkManager';
import { MMOEvents } from '../../managers/MMONetworkManager';
import { EventBus } from '../../utils/EventBus';
import { Colors, CSSColors, Fonts, FontSize, createDialogChrome, closeDialog } from './GameUITheme';
import { I18n } from '../../i18n/I18nService';
import { isMobile, getResponsiveDialogSize } from '../../utils/ResponsiveHelper';

interface AllianceMember {
  userId: string;
  username: string;
  rank: 'leader' | 'officer' | 'member';
  online: boolean;
  lastSeenDaysAgo: number;   // 0 = online now
  ships: number;
  stations: number;
  crew: number;
  joinedAt: number;
}

export class EnhancedAllianceDialog extends Phaser.GameObjects.Container {
  private dw: number;
  private dh: number;

  // Tab system
  private currentTab: 'overview' | 'members' | 'settings' | 'science' = 'overview';
  private contentContainer: Phaser.GameObjects.Container;

  // Services
  private mmoNetwork: MMONetworkManager;

  // HTML form overlays
  private formOverlay?: HTMLDivElement;
  private footerOverlay?: HTMLDivElement;
  private nameInput?: HTMLInputElement;
  private tagInput?: HTMLInputElement;
  private descInput?: HTMLTextAreaElement;
  private emblemInput?: HTMLInputElement;
  private previewContainer?: Phaser.GameObjects.Container;
  private previewTabBar?: Phaser.GameObjects.Container;
  private heroContainer?: Phaser.GameObjects.Container;
  private emblemDataUrl?: string;
  private removeBtn?: HTMLSpanElement;
  private createBtn?: HTMLButtonElement;
  private statusEl?: HTMLParagraphElement;
  private validationSummary?: HTMLSpanElement;
  private escHandler?: (e: KeyboardEvent) => void;
  private previewTab: 'map' | 'chat' | 'list' = 'map';
  private selectedChips: Set<string> = new Set();

  // Whether player has an alliance
  private hasAlliance: boolean;

  // Alliance data loaded from MMOStateManager
  private allianceData: {
    name: string;
    tag: string;
    leaderId: string;
    level: number;
    experience: number;
    nextLevelExp: number;
    description: string;
    foundedDate: string;
    members: AllianceMember[];
    recentActivity?: Array<{ text: string; timeAgo: string; type: string }>;
  };

  constructor(scene: Phaser.Scene, mmoState: MMOStateManager, mmoNetwork: MMONetworkManager) {
    const mobile = isMobile(scene);
    // Mobile: use responsive sizing so the dialog respects top-bar / bottom
    // browser-UI safe areas (header doesn't slip under the top bar, footer
    // button doesn't render behind the iPhone Chrome bottom toolbar).
    let dw: number;
    let dh: number;
    let dx: number;
    let dy: number;
    if (mobile) {
      const dims = getResponsiveDialogSize(720, 620, scene);
      dw = dims.width;
      dh = dims.height;
      dx = dims.x;
      dy = dims.y;
    } else {
      dw = 720;
      dh = 620;
      const { width, height } = scene.cameras.main;
      dx = (width - dw) / 2;
      dy = (height - dh) / 2;
    }
    super(scene, dx, dy);
    this.dw = dw;
    this.dh = dh;

    this.mmoNetwork = mmoNetwork;

    // Initialize alliance data from state
    const alliance = mmoState.getAlliance();
    this.hasAlliance = !!alliance;
    const myUserId = mmoState.getUserId() || 'me';
    this.allianceData = {
      name: alliance?.name || 'Unknown Alliance',
      tag: alliance?.tag || '???',
      leaderId: alliance?.leaderId || myUserId,
      level: 0,
      experience: 0,
      nextLevelExp: 0,
      description: (alliance as any)?.description || '',
      foundedDate: new Date().toISOString(),
      members: [],
    };

    // Themed dialog chrome (overlay + panel + header + title + close)
    createDialogChrome(scene, this, this.dw, this.dh, {
      title: this.hasAlliance ? I18n.t('dialog.alliance') : I18n.t('dialog.create_alliance'),
      onClose: () => this.close(),
    });

    // Content container
    this.contentContainer = scene.add.container(20, 60);
    this.add(this.contentContainer);

    if (this.hasAlliance) {
      this.showAllianceView();
    } else {
      // Show create form
      this.showCreateForm();
    }

    // Listen for alliance creation success
    EventBus.on(MMOEvents.ALLIANCE_CREATED, this.onAllianceCreated, this);
    // Listen for alliance details response
    EventBus.on(MMOEvents.ALLIANCE_DETAILS, this.onAllianceDetails, this);

    // Request real alliance data from server
    if (this.hasAlliance) {
      this.mmoNetwork.sendGetAllianceDetails();
    }

    // Set depth
    this.setDepth(3000);

    scene.add.existing(this);
  }

  // ═══════════════════════════════════════════
  //  ALLIANCE VIEW (when player has an alliance)
  // ═══════════════════════════════════════════

  private allianceTabContainer?: Phaser.GameObjects.Container;

  /** The player's rank within the alliance */
  private get myRank(): 'leader' | 'officer' | 'member' {
    const myId = this.allianceData.leaderId;
    const me = this.allianceData.members.find(m => m.userId === myId);
    return me?.rank ?? 'member';
  }

  /** Member tab sort state */
  private memberSort: 'rank' | 'last_seen' | 'ships' = 'rank';

  /** Confirmation overlay */
  private confirmOverlay?: Phaser.GameObjects.Container;

  private showAllianceView(): void {
    const CW = this.dw - 40; // content width

    // ── Emblem + Alliance name + tag header ──
    const emblemSize = 42;
    const emblem = this.scene.add.image(emblemSize / 2, emblemSize / 2, 'alliance-badge');
    emblem.setDisplaySize(emblemSize, emblemSize);
    this.contentContainer.add(emblem);

    const nameX = emblemSize + 12;
    const nameText = this.scene.add.text(nameX, 4, `${this.allianceData.name}`, {
      fontSize: '22px', fontStyle: '900', color: '#E0E8FF', fontFamily: Fonts.heading, letterSpacing: 1,
    });
    this.contentContainer.add(nameText);

    const tagText = this.scene.add.text(nameX + nameText.width + 10, 8, `[${this.allianceData.tag}]`, {
      fontSize: FontSize.lg, color: CSSColors.gold, fontFamily: Fonts.heading,
    });
    this.contentContainer.add(tagText);

    // Description (if set)
    if (this.allianceData.description) {
      const desc = this.scene.add.text(nameX, 30, this.allianceData.description, {
        fontSize: FontSize.sm, color: CSSColors.textDim, fontFamily: Fonts.body,
        wordWrap: { width: CW - nameX - 20 },
      });
      this.contentContainer.add(desc);
    }

    // ── Stats row ──
    const statsY = 56;
    const memberCount = this.allianceData.members.length;
    const onlineCount = this.allianceData.members.filter(m => m.online).length;
    const totalShips = this.allianceData.members.reduce((s, m) => s + m.ships, 0);
    const totalStations = this.allianceData.members.reduce((s, m) => s + m.stations, 0);

    const statItems = [
      { label: 'MEMBERS', value: `${memberCount}`, color: '#00FFFF' },
      { label: 'ONLINE', value: `${onlineCount}`, color: '#00FF88' },
      { label: 'SHIPS', value: `${totalShips}`, color: '#FFCC66' },
      { label: 'STATIONS', value: `${totalStations}`, color: '#FF8844' },
    ];

    const statW = CW / statItems.length;
    statItems.forEach((stat, i) => {
      const sx = i * statW;
      this.contentContainer.add(this.scene.add.text(sx + statW / 2, statsY, stat.value, {
        fontSize: FontSize.xl, fontStyle: '900', color: stat.color, fontFamily: Fonts.heading,
      }).setOrigin(0.5, 0));
      this.contentContainer.add(this.scene.add.text(sx + statW / 2, statsY + 22, stat.label, {
        fontSize: '9px', color: CSSColors.textDim, fontFamily: Fonts.body, letterSpacing: 2,
      }).setOrigin(0.5, 0));
    });

    // ── Tabs ──
    const tabY = statsY + 50;
    const tabDefs: { id: string; label: string }[] = [
      { id: 'overview', label: 'OVERVIEW' },
      { id: 'members', label: 'MEMBERS' },
      { id: 'science', label: 'SCIENCE' },
      { id: 'settings', label: 'SETTINGS' },
    ];
    const tabW = CW / tabDefs.length;

    tabDefs.forEach((tab, i) => {
      const tx = i * tabW;
      const isActive = tab.id === this.currentTab;

      const tabLabel = this.scene.add.text(tx + tabW / 2, tabY, tab.label, {
        fontSize: '10px', fontStyle: '900', color: isActive ? '#FFFFFF' : '#667799',
        fontFamily: Fonts.heading, letterSpacing: 2,
      });
      tabLabel.setOrigin(0.5, 0);
      this.contentContainer.add(tabLabel);

      if (isActive) {
        const underline = this.scene.add.graphics();
        underline.lineStyle(2, 0x00ffff, 1);
        underline.lineBetween(tx + 8, tabY + 18, tx + tabW - 8, tabY + 18);
        this.contentContainer.add(underline);
      }

      const hit = this.scene.add.zone(tx, tabY - 4, tabW, 24).setOrigin(0, 0);
      hit.setInteractive(new Phaser.Geom.Rectangle(0, 0, tabW, 24), Phaser.Geom.Rectangle.Contains);
      hit.on('pointerover', () => { if (!isActive) tabLabel.setColor('#BBCCEE'); this.scene.input.setDefaultCursor('pointer'); });
      hit.on('pointerout', () => { if (!isActive) tabLabel.setColor('#667799'); this.scene.input.setDefaultCursor('default'); });
      hit.on('pointerdown', () => {
        this.cleanupChatOverlay();
        this.currentTab = tab.id as any;
        this.contentContainer.removeAll(true);
        this.showAllianceView();
      });
      this.contentContainer.add(hit);
    });

    // Divider below tabs
    const divY = tabY + 24;
    const divGfx = this.scene.add.graphics();
    divGfx.lineStyle(1, 0x1e3a5e, 0.5);
    divGfx.lineBetween(0, divY, CW, divY);
    this.contentContainer.add(divGfx);

    // ── Tab content ──
    this.allianceTabContainer = this.scene.add.container(0, divY + 10);
    this.contentContainer.add(this.allianceTabContainer);

    switch (this.currentTab) {
      case 'overview': this.renderOverviewTab(CW); break;
      case 'members': this.renderMembersTab(CW); break;
      case 'science': this.renderScienceTab(CW); break;
      case 'settings': this.renderSettingsTab(CW); break;
    }
  }

  // ── Tab content renderers ──

  private renderOverviewTab(cw: number): void {
    const c = this.allianceTabContainer!;
    let y = 0;

    // ── Recent activity feed (from server data) ──
    c.add(this.scene.add.text(0, y, 'RECENT ACTIVITY', {
      fontSize: '10px', fontStyle: '900', color: '#667799', fontFamily: Fonts.heading, letterSpacing: 2,
    }));
    y += 24;

    const activities = this.allianceData.recentActivity || [];

    if (activities.length === 0) {
      c.add(this.scene.add.text(cw / 2, y + 20, 'No recent activity', {
        fontSize: FontSize.md, color: CSSColors.textDim, fontFamily: Fonts.body,
      }).setOrigin(0.5, 0));
      y += 60;
    } else {
      const typeColors: Record<string, number> = {
        member_joined: 0x44ff44,
        member_left: 0xff6644,
        member_kicked: 0xff4444,
        member_promoted: 0x6688CC,
        alliance_created: 0xFFAA00,
      };

      activities.slice(0, 8).forEach(act => {
        const dotColor = typeColors[act.type] || 0x667799;
        const dot = this.scene.add.circle(6, y + 7, 3, dotColor);
        c.add(dot);
        c.add(this.scene.add.text(18, y, act.text, {
          fontSize: FontSize.sm, color: CSSColors.textMuted, fontFamily: Fonts.body,
        }));
        c.add(this.scene.add.text(cw - 8, y, act.timeAgo, {
          fontSize: '9px', color: '#445566', fontFamily: Fonts.body,
        }).setOrigin(1, 0));
        y += 22;
      });
    }

    // ── Alliance benefits — fixed at bottom, 2 columns ──
    const TAB_CONTENT_H = this.dh - 280;
    const benefitsY = TAB_CONTENT_H - 90; // pinned near bottom

    c.add(this.scene.add.text(0, benefitsY, 'ALLIANCE BENEFITS', {
      fontSize: '10px', fontStyle: '900', color: '#667799', fontFamily: Fonts.heading, letterSpacing: 2,
    }));

    const benefits = [
      'Shared vision & fog of war',
      'Alliance chat & coordination',
      'Alliance tax & treasury',
      'Station raids & territory bonuses',
      'Alliance grows with stations',
      'Max 100 members',
    ];
    const colW = cw / 2;
    benefits.forEach((b, i) => {
      const col = i < 3 ? 0 : 1;
      const row = i % 3;
      const bx = col * colW;
      const by = benefitsY + 24 + row * 20;
      c.add(this.scene.add.text(bx + 4, by, '\u2022', { fontSize: FontSize.sm, color: '#334466', fontFamily: Fonts.body }));
      c.add(this.scene.add.text(bx + 16, by, b, { fontSize: FontSize.sm, color: CSSColors.textMuted, fontFamily: Fonts.body }));
    });
  }

  private renderMembersTab(cw: number): void {
    const c = this.allianceTabContainer!;
    let y = 0;

    const onlineCount = this.allianceData.members.filter(m => m.online).length;
    c.add(this.scene.add.text(0, y, `${this.allianceData.members.length} MEMBERS  ·  ${onlineCount} ONLINE`, {
      fontSize: '10px', fontStyle: '900', color: '#667799', fontFamily: Fonts.heading, letterSpacing: 2,
    }));
    y += 22;

    // Sort label (d7) — right-aligned
    const sortLabels: Record<string, string> = { rank: 'Rank', last_seen: 'Last Seen', ships: 'Ships' };
    const sortText = this.scene.add.text(cw - 8, y, `Sort: ${sortLabels[this.memberSort]}`, {
      fontSize: '9px', color: '#556677', fontFamily: Fonts.heading, letterSpacing: 1,
    }).setOrigin(1, 0);
    c.add(sortText);
    const sortHit = this.scene.add.zone(cw - sortText.width - 16, y - 2, sortText.width + 16, 18).setOrigin(0, 0);
    sortHit.setInteractive(new Phaser.Geom.Rectangle(0, 0, sortText.width + 16, 18), Phaser.Geom.Rectangle.Contains);
    sortHit.on('pointerover', () => { sortText.setColor('#AABBCC'); this.scene.input.setDefaultCursor('pointer'); });
    sortHit.on('pointerout', () => { sortText.setColor('#556677'); this.scene.input.setDefaultCursor('default'); });
    sortHit.on('pointerdown', () => {
      const opts: Array<'rank' | 'last_seen' | 'ships'> = ['rank', 'last_seen', 'ships'];
      const idx = opts.indexOf(this.memberSort);
      this.memberSort = opts[(idx + 1) % opts.length];
      this.contentContainer.removeAll(true);
      this.showAllianceView();
    });
    c.add(sortHit);
    y += 22;

    // ── Column headers ──
    const colName = 30;
    const colShips = cw * 0.45;
    const colStations = cw * 0.58;
    const colSeen = cw * 0.72;

    c.add(this.scene.add.text(colName, y, 'NAME', { fontSize: '8px', color: '#445566', fontFamily: Fonts.heading, letterSpacing: 1 }));
    c.add(this.scene.add.text(colShips, y, 'SHIPS', { fontSize: '8px', color: '#445566', fontFamily: Fonts.heading, letterSpacing: 1 }));
    c.add(this.scene.add.text(colStations, y, 'STATIONS', { fontSize: '8px', color: '#445566', fontFamily: Fonts.heading, letterSpacing: 1 }));
    c.add(this.scene.add.text(colSeen, y, 'LAST SEEN', { fontSize: '8px', color: '#445566', fontFamily: Fonts.heading, letterSpacing: 1 }));
    y += 18;

    // ── Sort members ──
    let filtered = [...this.allianceData.members];

    const rankOrder: Record<string, number> = { leader: 0, officer: 1, member: 2 };
    if (this.memberSort === 'rank') {
      filtered.sort((a, b) => rankOrder[a.rank] - rankOrder[b.rank]);
    } else if (this.memberSort === 'last_seen') {
      filtered.sort((a, b) => a.lastSeenDaysAgo - b.lastSeenDaysAgo);
    } else if (this.memberSort === 'ships') {
      filtered.sort((a, b) => b.ships - a.ships);
    }

    const canKick = this.myRank === 'leader' || this.myRank === 'officer';
    const canPromote = this.myRank === 'leader';

    if (filtered.length === 0) {
      c.add(this.scene.add.text(cw / 2, y + 40, 'Loading members...', {
        fontSize: FontSize.md, color: CSSColors.textDim, fontFamily: Fonts.body,
      }).setOrigin(0.5, 0));
    }

    filtered.forEach(member => {
      const cardH = 52;
      const bg = this.scene.add.graphics();
      bg.fillStyle(member.online ? 0x0a1a10 : 0x0a0e1e, 0.5);
      bg.fillRoundedRect(0, y, cw, cardH, 6);
      bg.lineStyle(1, member.online ? 0x225522 : 0x1e2a40, 0.4);
      bg.strokeRoundedRect(0, y, cw, cardH, 6);
      c.add(bg);

      const dotColor = member.online ? 0x44ff44 : 0x444444;
      c.add(this.scene.add.circle(16, y + 16, 4, dotColor));

      c.add(this.scene.add.text(colName, y + 8, member.username, {
        fontSize: FontSize.md, fontStyle: '900', color: '#E0E8FF', fontFamily: Fonts.heading,
      }));

      const rankColors: Record<string, string> = { leader: '#FFAA00', officer: '#6688CC', member: '#667799' };
      c.add(this.scene.add.text(colName, y + 28, member.rank.toUpperCase(), {
        fontSize: '9px', color: rankColors[member.rank] || '#667799', fontFamily: Fonts.heading, letterSpacing: 1,
      }));

      c.add(this.scene.add.text(colShips, y + cardH / 2, `${member.ships}`, {
        fontSize: FontSize.md, fontStyle: '900', color: '#FFCC66', fontFamily: Fonts.heading,
      }).setOrigin(0, 0.5));

      c.add(this.scene.add.text(colStations, y + cardH / 2, `${member.stations}`, {
        fontSize: FontSize.md, fontStyle: '900', color: '#FF8844', fontFamily: Fonts.heading,
      }).setOrigin(0, 0.5));

      let seenStr = 'Online';
      let seenColor = '#44ff44';
      if (!member.online) {
        seenStr = 'Offline';
        seenColor = '#667799';
      }
      c.add(this.scene.add.text(colSeen, y + cardH / 2, seenStr, {
        fontSize: FontSize.sm, color: seenColor, fontFamily: Fonts.body,
      }).setOrigin(0, 0.5));

      // Action buttons — never shown for the leader row
      if (member.rank !== 'leader') {
        const actionX = cw - 12;

        if (canKick && (member.rank === 'member' || this.myRank === 'leader')) {
          const kickText = this.scene.add.text(actionX, y + 10, 'KICK', {
            fontSize: '9px', fontStyle: '900', color: '#663333', fontFamily: Fonts.heading, letterSpacing: 1,
          }).setOrigin(1, 0);
          c.add(kickText);
          const kickHit = this.scene.add.zone(actionX - 40, y + 6, 44, 18).setOrigin(0, 0);
          kickHit.setInteractive(new Phaser.Geom.Rectangle(0, 0, 44, 18), Phaser.Geom.Rectangle.Contains);
          kickHit.on('pointerover', () => { kickText.setColor('#FF4444'); this.scene.input.setDefaultCursor('pointer'); });
          kickHit.on('pointerout', () => { kickText.setColor('#663333'); this.scene.input.setDefaultCursor('default'); });
          kickHit.on('pointerdown', () => {
            this.showConfirm(`Kick ${member.username}?`, `They will be removed from the alliance.`, () => {
              this.allianceData.members = this.allianceData.members.filter(m => m.userId !== member.userId);
              this.contentContainer.removeAll(true);
              this.showAllianceView();
            });
          });
          c.add(kickHit);
        }

        if (canPromote && member.rank === 'member') {
          const promoteText = this.scene.add.text(actionX, y + 30, 'PROMOTE', {
            fontSize: '9px', fontStyle: '900', color: '#335566', fontFamily: Fonts.heading, letterSpacing: 1,
          }).setOrigin(1, 0);
          c.add(promoteText);
          const promoteHit = this.scene.add.zone(actionX - 56, y + 26, 60, 18).setOrigin(0, 0);
          promoteHit.setInteractive(new Phaser.Geom.Rectangle(0, 0, 60, 18), Phaser.Geom.Rectangle.Contains);
          promoteHit.on('pointerover', () => { promoteText.setColor('#6688FF'); this.scene.input.setDefaultCursor('pointer'); });
          promoteHit.on('pointerout', () => { promoteText.setColor('#335566'); this.scene.input.setDefaultCursor('default'); });
          promoteHit.on('pointerdown', () => {
            member.rank = 'officer';
            this.contentContainer.removeAll(true);
            this.showAllianceView();
          });
          c.add(promoteHit);
        }
      }

      y += cardH + 6;
    });
  }

  private cleanupChatOverlay(): void {
    // No-op — chat moved to Communication panel
  }

  private renderScienceTab(cw: number): void {
    const c = this.allianceTabContainer!;
    let y = 0;

    c.add(this.scene.add.text(cw / 2, y, 'COMING SOON', {
      fontSize: FontSize.lg, fontStyle: '900', color: '#667799', fontFamily: Fonts.heading, letterSpacing: 4,
    }).setOrigin(0.5, 0));
    y += 28;

    c.add(this.scene.add.text(cw / 2, y, 'Research technologies to unlock alliance-wide bonuses.', {
      fontSize: FontSize.sm, color: CSSColors.textDim, fontFamily: Fonts.body, align: 'center',
    }).setOrigin(0.5, 0));
    y += 32;

    // ── Greyed-out tech tree categories ──
    const categories = [
      { name: 'FLEET TECHNOLOGY', desc: 'Ship speed, armor, weapon damage', icon: '\u2694', color: 0xFF8844 },
      { name: 'STATION TECHNOLOGY', desc: 'Station defense, storage, range', icon: '\u{1F3F0}', color: 0x6688CC },
      { name: 'ECONOMY RESEARCH', desc: 'Mining yield, trade bonuses, LP share', icon: '\u{1F4B0}', color: 0x00FF88 },
      { name: 'RECON & INTEL', desc: 'Vision range, scan radius, fog reveal', icon: '\u{1F50D}', color: 0x00FFFF },
      { name: 'SPECIAL PROJECTS', desc: 'Superweapons, wormhole gates, titans', icon: '\u{2B50}', color: 0xFFCC66 },
    ];

    categories.forEach(cat => {
      const cardH = 56;
      const bg = this.scene.add.graphics();
      bg.fillStyle(0x0a0e1e, 0.4);
      bg.fillRoundedRect(0, y, cw, cardH, 6);
      bg.lineStyle(1, 0x1e2a40, 0.3);
      bg.strokeRoundedRect(0, y, cw, cardH, 6);
      c.add(bg);

      // Icon circle
      const iconBg = this.scene.add.graphics();
      iconBg.fillStyle(cat.color, 0.12);
      iconBg.fillCircle(30, y + cardH / 2, 18);
      c.add(iconBg);
      c.add(this.scene.add.text(30, y + cardH / 2, cat.icon, {
        fontSize: '18px', fontFamily: Fonts.body,
      }).setOrigin(0.5, 0.5));

      // Name
      c.add(this.scene.add.text(58, y + 12, cat.name, {
        fontSize: '10px', fontStyle: '900', color: '#556677', fontFamily: Fonts.heading, letterSpacing: 2,
      }));

      // Description
      c.add(this.scene.add.text(58, y + 30, cat.desc, {
        fontSize: FontSize.sm, color: '#334455', fontFamily: Fonts.body,
      }));

      // Locked indicator
      c.add(this.scene.add.text(cw - 14, y + cardH / 2, '\u{1F512}', {
        fontSize: '14px', fontFamily: Fonts.body,
      }).setOrigin(1, 0.5));

      y += cardH + 6;
    });
  }

  private renderSettingsTab(cw: number): void {
    const c = this.allianceTabContainer!;
    let y = 0;
    const isLeader = this.myRank === 'leader';
    const isOfficer = this.myRank === 'officer';

    // ── Invitation code ──
    c.add(this.scene.add.text(0, y, 'INVITATION CODE', {
      fontSize: '10px', fontStyle: '900', color: '#667799', fontFamily: Fonts.heading, letterSpacing: 2,
    }));
    y += 22;

    const inviteCode = `${this.allianceData.tag}-${Math.random().toString(36).substring(2, 8).toUpperCase()}`;
    const codeBg = this.scene.add.graphics();
    codeBg.fillStyle(0x0a1428, 0.8); codeBg.fillRoundedRect(0, y, cw, 34, 4);
    codeBg.lineStyle(1, 0x1e3a5e, 0.5); codeBg.strokeRoundedRect(0, y, cw, 34, 4);
    c.add(codeBg);
    c.add(this.scene.add.text(12, y + 9, inviteCode, {
      fontSize: FontSize.md, fontStyle: '900', color: '#FDB15B', fontFamily: Fonts.heading, letterSpacing: 2,
    }));
    const copyText = this.scene.add.text(cw - 12, y + 17, 'COPY', {
      fontSize: '9px', fontStyle: '900', color: '#6688CC', fontFamily: Fonts.heading, letterSpacing: 1,
    }).setOrigin(1, 0.5);
    c.add(copyText);
    const copyHit = this.scene.add.zone(cw - 60, y, 60, 34).setOrigin(0, 0);
    copyHit.setInteractive(new Phaser.Geom.Rectangle(0, 0, 60, 34), Phaser.Geom.Rectangle.Contains);
    copyHit.on('pointerover', () => { copyText.setColor('#FFFFFF'); this.scene.input.setDefaultCursor('pointer'); });
    copyHit.on('pointerout', () => { copyText.setColor('#6688CC'); this.scene.input.setDefaultCursor('default'); });
    copyHit.on('pointerdown', () => {
      navigator.clipboard?.writeText(inviteCode);
      copyText.setText('COPIED!');
      this.scene.time.delayedCall(1500, () => { if (copyText.active) copyText.setText('COPY'); });
    });
    c.add(copyHit);
    c.add(this.scene.add.text(0, y + 40, 'Share this code with players to invite them.', {
      fontSize: FontSize.sm, color: CSSColors.textDim, fontFamily: Fonts.body,
    }));
    y += 64;

    // ── Description (d8 — editable for leader/officer) ──
    c.add(this.scene.add.text(0, y, 'DESCRIPTION', {
      fontSize: '10px', fontStyle: '900', color: '#667799', fontFamily: Fonts.heading, letterSpacing: 2,
    }));
    if (isLeader || isOfficer) {
      const editDescText = this.scene.add.text(cw - 8, y, 'EDIT', {
        fontSize: '9px', fontStyle: '900', color: '#335566', fontFamily: Fonts.heading, letterSpacing: 1,
      }).setOrigin(1, 0);
      c.add(editDescText);
      const editDescHit = this.scene.add.zone(cw - 44, y - 2, 44, 18).setOrigin(0, 0);
      editDescHit.setInteractive(new Phaser.Geom.Rectangle(0, 0, 44, 18), Phaser.Geom.Rectangle.Contains);
      editDescHit.on('pointerover', () => { editDescText.setColor('#6688FF'); this.scene.input.setDefaultCursor('pointer'); });
      editDescHit.on('pointerout', () => { editDescText.setColor('#335566'); this.scene.input.setDefaultCursor('default'); });
      editDescHit.on('pointerdown', () => {
        // TODO: open inline edit overlay for description
        editDescText.setText('TODO');
        this.scene.time.delayedCall(1000, () => { if (editDescText.active) editDescText.setText('EDIT'); });
      });
      c.add(editDescHit);
    }
    y += 22;
    c.add(this.scene.add.text(0, y, this.allianceData.description || 'No description set', {
      fontSize: FontSize.sm, color: CSSColors.textLight, fontFamily: Fonts.body, wordWrap: { width: cw },
    }));
    y += 40;

    // ── Permissions (d8 — toggleable for leader) ──
    c.add(this.scene.add.text(0, y, 'PERMISSIONS', {
      fontSize: '10px', fontStyle: '900', color: '#667799', fontFamily: Fonts.heading, letterSpacing: 2,
    }));
    y += 22;

    const perms = [
      { label: 'Officers can invite members', enabled: true },
      { label: 'Members can view alliance chat', enabled: true },
      { label: 'Members can donate resources', enabled: true },
    ];
    perms.forEach(p => {
      const check = p.enabled ? '\u2713' : '\u2717';
      const checkColor = p.enabled ? '#00FF88' : '#663333';
      const checkText = this.scene.add.text(20, y, check, { fontSize: FontSize.sm, color: checkColor, fontFamily: Fonts.body });
      c.add(checkText);
      c.add(this.scene.add.text(36, y, p.label, { fontSize: FontSize.sm, color: CSSColors.textMuted, fontFamily: Fonts.body }));

      if (isLeader) {
        const permHit = this.scene.add.zone(0, y - 2, cw, 20).setOrigin(0, 0);
        permHit.setInteractive(new Phaser.Geom.Rectangle(0, 0, cw, 20), Phaser.Geom.Rectangle.Contains);
        permHit.on('pointerover', () => this.scene.input.setDefaultCursor('pointer'));
        permHit.on('pointerout', () => this.scene.input.setDefaultCursor('default'));
        permHit.on('pointerdown', () => {
          p.enabled = !p.enabled;
          checkText.setText(p.enabled ? '\u2713' : '\u2717');
          checkText.setColor(p.enabled ? '#00FF88' : '#663333');
        });
        c.add(permHit);
      }
      y += 22;
    });

    y += 16;

    // ── Browse Alliances link ──
    const browseLabel = this.scene.add.text(0, y, 'BROWSE ALLIANCES', {
      fontSize: '10px', fontStyle: '900', color: '#00FFFF', fontFamily: Fonts.heading, letterSpacing: 1,
    });
    c.add(browseLabel);
    const browseHit = this.scene.add.zone(0, y - 4, browseLabel.width + 16, 22).setOrigin(0, 0);
    browseHit.setInteractive(new Phaser.Geom.Rectangle(0, 0, browseLabel.width + 16, 22), Phaser.Geom.Rectangle.Contains);
    browseHit.on('pointerover', () => { browseLabel.setColor('#FFFFFF'); this.scene.input.setDefaultCursor('pointer'); });
    browseHit.on('pointerout', () => { browseLabel.setColor('#00FFFF'); this.scene.input.setDefaultCursor('default'); });
    browseHit.on('pointerdown', () => {
      this.close();
      EventBus.emit('mmo-browse-alliances');
    });
    c.add(browseHit);
    y += 30;

    // ── Danger zone (d9, d16) ──
    c.add(this.scene.add.text(0, y, 'DANGER ZONE', {
      fontSize: '10px', fontStyle: '900', color: '#663333', fontFamily: Fonts.heading, letterSpacing: 2,
    }));
    y += 22;

    // Leave alliance (all members)
    this.addDangerButton(c, 0, y, cw, 'LEAVE ALLIANCE', () => {
      this.showConfirm('Leave alliance?', 'You will lose access to alliance chat, shared vision, and benefits.', () => {
        this.mmoNetwork.sendAllianceLeave();
        this.close();
      });
    });
    y += 44;

    // Leader-only: Transfer + Disband
    if (isLeader) {
      this.addDangerButton(c, 0, y, cw, 'TRANSFER LEADERSHIP', () => {
        this.showConfirm('Transfer leadership?', 'Choose an officer to become the new leader. (Not yet implemented)', () => {
          // TODO: wire to server
        });
      });
      y += 44;

      this.addDangerButton(c, 0, y, cw, 'DISBAND ALLIANCE', () => {
        this.showConfirm('Disband alliance?', 'This action is permanent. All members will be removed and the alliance deleted.', () => {
          // TODO: wire to server disband
        });
      });
    }
  }

  /** Helper: render a red danger-zone button */
  private addDangerButton(c: Phaser.GameObjects.Container, x: number, y: number, w: number, label: string, onClick: () => void): void {
    const bg = this.scene.add.graphics();
    bg.fillStyle(0x331111, 0.6); bg.fillRoundedRect(x, y, w, 36, 6);
    bg.lineStyle(1, 0x662222, 0.5); bg.strokeRoundedRect(x, y, w, 36, 6);
    c.add(bg);
    const text = this.scene.add.text(x + w / 2, y + 18, label, {
      fontSize: FontSize.sm, fontStyle: '900', color: '#FF4444', fontFamily: Fonts.heading, letterSpacing: 2,
    }).setOrigin(0.5);
    c.add(text);
    const hit = this.scene.add.zone(x, y, w, 36).setOrigin(0, 0);
    hit.setInteractive(new Phaser.Geom.Rectangle(0, 0, w, 36), Phaser.Geom.Rectangle.Contains);
    hit.on('pointerover', () => {
      bg.clear(); bg.fillStyle(0x441111, 0.8); bg.fillRoundedRect(x, y, w, 36, 6);
      bg.lineStyle(1, 0x882222, 0.7); bg.strokeRoundedRect(x, y, w, 36, 6);
      this.scene.input.setDefaultCursor('pointer');
    });
    hit.on('pointerout', () => {
      bg.clear(); bg.fillStyle(0x331111, 0.6); bg.fillRoundedRect(x, y, w, 36, 6);
      bg.lineStyle(1, 0x662222, 0.5); bg.strokeRoundedRect(x, y, w, 36, 6);
      this.scene.input.setDefaultCursor('default');
    });
    hit.on('pointerdown', onClick);
    c.add(hit);
  }

  /** Show a confirmation overlay before destructive actions (d16) */
  private showConfirm(title: string, message: string, onConfirm: () => void): void {
    this.hideConfirm();
    const CW = this.dw - 40;
    const overlay = this.scene.add.container(0, 0);

    // Dimmed backdrop over the entire dialog
    const backdrop = this.scene.add.graphics();
    backdrop.fillStyle(0x000000, 0.6);
    backdrop.fillRect(-20, -60, this.dw, this.dh);
    overlay.add(backdrop);

    const backdropHit = this.scene.add.zone(-20, -60, this.dw, this.dh).setOrigin(0, 0);
    backdropHit.setInteractive(new Phaser.Geom.Rectangle(0, 0, this.dw, this.dh), Phaser.Geom.Rectangle.Contains);
    backdropHit.on('pointerdown', () => this.hideConfirm());
    overlay.add(backdropHit);

    // Confirm card
    const cardW = 340;
    const cardH = 160;
    const cardX = (CW - cardW) / 2;
    const cardY = (this.dh - 200) / 2 - 60;

    const cardBg = this.scene.add.graphics();
    cardBg.fillStyle(0x0e1a2e, 1); cardBg.fillRoundedRect(cardX, cardY, cardW, cardH, 8);
    cardBg.lineStyle(2, 0x662222, 0.8); cardBg.strokeRoundedRect(cardX, cardY, cardW, cardH, 8);
    overlay.add(cardBg);

    overlay.add(this.scene.add.text(cardX + cardW / 2, cardY + 24, title, {
      fontSize: FontSize.lg, fontStyle: '900', color: '#FF6644', fontFamily: Fonts.heading,
    }).setOrigin(0.5, 0));

    overlay.add(this.scene.add.text(cardX + cardW / 2, cardY + 52, message, {
      fontSize: FontSize.sm, color: CSSColors.textMuted, fontFamily: Fonts.body,
      wordWrap: { width: cardW - 40 }, align: 'center',
    }).setOrigin(0.5, 0));

    // Cancel button
    const cancelText = this.scene.add.text(cardX + cardW * 0.3, cardY + cardH - 36, 'CANCEL', {
      fontSize: FontSize.sm, fontStyle: '900', color: '#667799', fontFamily: Fonts.heading, letterSpacing: 1,
    }).setOrigin(0.5, 0.5);
    overlay.add(cancelText);
    const cancelHit = this.scene.add.zone(cardX + cardW * 0.3 - 40, cardY + cardH - 50, 80, 28).setOrigin(0, 0);
    cancelHit.setInteractive(new Phaser.Geom.Rectangle(0, 0, 80, 28), Phaser.Geom.Rectangle.Contains);
    cancelHit.on('pointerover', () => { cancelText.setColor('#FFFFFF'); this.scene.input.setDefaultCursor('pointer'); });
    cancelHit.on('pointerout', () => { cancelText.setColor('#667799'); this.scene.input.setDefaultCursor('default'); });
    cancelHit.on('pointerdown', () => this.hideConfirm());
    overlay.add(cancelHit);

    // Confirm button
    const confirmBg = this.scene.add.graphics();
    confirmBg.fillStyle(0x661111, 0.8); confirmBg.fillRoundedRect(cardX + cardW * 0.7 - 50, cardY + cardH - 50, 100, 28, 4);
    overlay.add(confirmBg);
    const confirmText = this.scene.add.text(cardX + cardW * 0.7, cardY + cardH - 36, 'CONFIRM', {
      fontSize: FontSize.sm, fontStyle: '900', color: '#FF4444', fontFamily: Fonts.heading, letterSpacing: 1,
    }).setOrigin(0.5, 0.5);
    overlay.add(confirmText);
    const confirmHit = this.scene.add.zone(cardX + cardW * 0.7 - 50, cardY + cardH - 50, 100, 28).setOrigin(0, 0);
    confirmHit.setInteractive(new Phaser.Geom.Rectangle(0, 0, 100, 28), Phaser.Geom.Rectangle.Contains);
    confirmHit.on('pointerover', () => {
      confirmBg.clear(); confirmBg.fillStyle(0x881111, 1); confirmBg.fillRoundedRect(cardX + cardW * 0.7 - 50, cardY + cardH - 50, 100, 28, 4);
      confirmText.setColor('#FFFFFF'); this.scene.input.setDefaultCursor('pointer');
    });
    confirmHit.on('pointerout', () => {
      confirmBg.clear(); confirmBg.fillStyle(0x661111, 0.8); confirmBg.fillRoundedRect(cardX + cardW * 0.7 - 50, cardY + cardH - 50, 100, 28, 4);
      confirmText.setColor('#FF4444'); this.scene.input.setDefaultCursor('default');
    });
    confirmHit.on('pointerdown', () => { this.hideConfirm(); onConfirm(); });
    overlay.add(confirmHit);

    this.contentContainer.add(overlay);
    this.confirmOverlay = overlay;
  }

  private hideConfirm(): void {
    if (this.confirmOverlay) {
      this.confirmOverlay.destroy(true);
      this.confirmOverlay = undefined;
      this.scene.input.setDefaultCursor('default');
    }
  }

  // ── Form validation state ──
  private formIsDirty(): boolean {
    return !!(
      (this.nameInput?.value && this.nameInput.value.length > 0) ||
      (this.tagInput?.value && this.tagInput.value.length > 0) ||
      (this.descInput?.value && this.descInput.value.length > 0) ||
      this.emblemDataUrl
    );
  }

  private isFormValid(): boolean {
    const name = this.nameInput?.value.trim() || '';
    const tag = this.tagInput?.value.trim() || '';
    return name.length >= 3 && name.length <= 20 && tag.length === 3;
  }

  private refreshCTAState(): void {
    if (!this.createBtn) return;
    const valid = this.isFormValid();
    this.createBtn.disabled = !valid;
    if (valid) {
      this.createBtn.style.opacity = '1';
      this.createBtn.style.cursor = 'pointer';
      this.createBtn.textContent = 'CREATE ALLIANCE (FREE)';
    } else {
      this.createBtn.style.opacity = '0.35';
      this.createBtn.style.cursor = 'not-allowed';
      this.createBtn.textContent = 'CREATE ALLIANCE (FREE)';
    }
  }

  private showCreateForm(): void {
    this.contentContainer.removeAll(true);
    const cw = this.dw - 40;
    const mobile = isMobile(this.scene);

    // ── Hero subtitle (desktop only — mobile has no room above the form) ──
    if (!mobile) {
      this.heroContainer = this.scene.add.container(0, 0);
      const h1 = this.scene.add.text(cw / 2, -48, 'Forge your alliance, claim the frontier', {
        fontSize: FontSize.xl, color: '#ffffff', fontFamily: Fonts.heading, align: 'center',
      });
      h1.setOrigin(0.5, 0);
      this.heroContainer.add(h1);
      const h2 = this.scene.add.text(cw / 2, -22, 'conquer together, share the spoils.', {
        fontSize: FontSize.md, color: CSSColors.cyan, fontFamily: Fonts.body, fontStyle: 'italic', align: 'center',
      });
      h2.setOrigin(0.5, 0);
      this.heroContainer.add(h2);
      this.contentContainer.add(this.heroContainer);
    }

    // ── Layout constants ──
    // Mobile: single-column form fills the dialog width, no preview shown.
    // Desktop: two-column with preview pane on the right.
    const FORM_W = mobile ? cw : 380;
    const GAP = mobile ? 0 : 36;
    const PREVIEW_X = FORM_W + GAP;
    const PREVIEW_W = mobile ? 0 : (cw - FORM_W - GAP);

    // ── Screen-space positioning ──
    const canvas = this.scene.game.canvas;
    const canvasRect = canvas.getBoundingClientRect();
    const gameW = this.scene.cameras.main.width;
    const gameH = this.scene.cameras.main.height;
    const sx = canvasRect.width / gameW;
    const sy = canvasRect.height / gameH;

    // On mobile we skip the hero subtitle, so the form can start right
    // below the dialog header (~70px) instead of after the hero (~145px).
    const FORM_TOP_OFFSET = mobile ? 70 : 145;
    const formLeft = canvasRect.left + (this.x + 24) * sx;
    const formTop = canvasRect.top + (this.y + FORM_TOP_OFFSET) * sy;
    const formW = FORM_W * sx;
    const formH = (this.dh - FORM_TOP_OFFSET - 72) * sy;

    // ── Design tokens ──
    const g = (n: number) => n * sy;

    // ── Build form HTML overlay (left column) ──
    const overlay = document.createElement('div');
    this.formOverlay = overlay;
    overlay.style.cssText = `
      position:fixed; left:${formLeft}px; top:${formTop}px;
      width:${formW}px; height:${formH}px;
      z-index:100001; pointer-events:auto;
      display:flex; flex-direction:column;
      font-family:${Fonts.body}; color:#fff;
      overflow-y:auto; overflow-x:hidden;
    `;

    const stop = (e: Event) => e.stopPropagation();
    ['mousedown','mouseup','click','touchstart','touchend','keydown','keyup','wheel']
      .forEach(evt => overlay.addEventListener(evt, stop));
    overlay.addEventListener('focusin', () => {
      if (this.scene.input.keyboard) this.scene.input.keyboard.enabled = false;
      EventBus.emit('chat-input-focused');
    });
    overlay.addEventListener('focusout', () => {
      if (this.scene.input.keyboard) this.scene.input.keyboard.enabled = true;
      EventBus.emit('chat-input-unfocused');
    });

    // Esc to close
    this.escHandler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (this.formIsDirty()) {
          if (this.statusEl) {
            this.statusEl.textContent = 'Press Esc again to discard';
            this.statusEl.style.color = '#ffaa44';
            const secondEsc = (e2: KeyboardEvent) => {
              if (e2.key === 'Escape') { document.removeEventListener('keydown', secondEsc); this.close(); }
            };
            document.removeEventListener('keydown', this.escHandler!);
            document.addEventListener('keydown', secondEsc);
          }
        } else { this.close(); }
      }
    };
    document.addEventListener('keydown', this.escHandler);

    // ── Style utilities ──
    const inputBg = '#0a1525';
    const inputCss = `
      background:${inputBg}; color:#e0e8ff; border:1.5px solid #1e3a5e; border-radius:6px;
      font-family:${Fonts.body}; outline:none; box-sizing:border-box;
      caret-color:#00ffff; transition:border-color .15s, box-shadow .15s;
    `;
    const addFB = (el: HTMLElement) => {
      el.addEventListener('focus', () => { el.style.borderColor='#FDB15B'; el.style.boxShadow='0 0 8px rgba(253,177,91,0.35)'; });
      el.addEventListener('blur', () => { el.style.borderColor='#1e3a5e'; el.style.boxShadow='none'; });
    };
    const mkLabel = (html: string, cid?: string, cmax?: number) => {
      const r = document.createElement('div');
      r.style.cssText = `font-family:${Fonts.heading};font-size:${13*sy}px;color:rgba(136,221,255,0.9);margin:0;display:flex;justify-content:space-between;align-items:baseline;letter-spacing:0.5px;`;
      let h = `<span>${html}</span>`;
      if (cid && cmax) h += `<span style="font-family:${Fonts.body};color:rgba(68,85,102,0.8);font-size:${12*sy}px" id="${cid}">0 / ${cmax}</span>`;
      r.innerHTML = h;
      return r;
    };
    const mkSpacer = (h: number) => { const d = document.createElement('div'); d.style.cssText = `height:${h}px;flex-shrink:0;`; return d; };
    const mkDivider = () => { const d = document.createElement('div'); d.style.cssText = `height:1px;background:linear-gradient(90deg,transparent,#1e3a5e 30%,#2a5a8a 50%,#1e3a5e 70%,transparent);margin:${g(14)}px 0;flex-shrink:0;`; return d; };

    // ════════════════════════════════════════════
    //  ALLIANCE NAME (full width)
    // ════════════════════════════════════════════
    overlay.appendChild(mkLabel('ALLIANCE NAME', 'sc-name-cnt', 14));
    overlay.appendChild(mkSpacer(g(10)));

    const nameWrap = document.createElement('div');
    nameWrap.style.cssText = 'position:relative;';
    this.nameInput = document.createElement('input');
    this.nameInput.type = 'text';
    this.nameInput.placeholder = 'Enter alliance name...';
    this.nameInput.maxLength = 20;
    this.nameInput.tabIndex = 1;
    this.nameInput.style.cssText = `${inputCss} width:100%; height:${g(40)}px; padding:0 ${34*sx}px 0 ${14*sx}px; font-size:${14*sy}px;`;
    addFB(this.nameInput);
    const nameIcon = document.createElement('span');
    nameIcon.style.cssText = `position:absolute;right:${10*sx}px;top:50%;transform:translateY(-50%);font-size:${16*sy}px;opacity:0;pointer-events:none;transition:opacity .2s;`;
    nameWrap.appendChild(this.nameInput);
    nameWrap.appendChild(nameIcon);
    overlay.appendChild(nameWrap);

    const nameMsg = document.createElement('div');
    nameMsg.style.cssText = `font-size:${12*sy}px;color:#556677;margin-top:${g(6)}px;min-height:${g(16)}px;transition:color .15s;`;
    nameMsg.textContent = '3\u201320 characters';
    overlay.appendChild(nameMsg);

    this.nameInput.addEventListener('input', () => {
      if (this.nameInput?.value.startsWith(' ')) this.nameInput.value = this.nameInput.value.trimStart();
      if (this.nameInput?.value.includes('  ')) this.nameInput.value = this.nameInput.value.replace(/  +/g, ' ');
      const len = this.nameInput?.value.length || 0;
      const c = overlay.querySelector('#sc-name-cnt');
      if (c) { c.textContent = `${len} / 20`; (c as HTMLElement).style.color = len >= 3 ? '#00ff88' : 'rgba(68,85,102,0.8)'; }
      if (len === 0) { nameIcon.style.opacity = '0'; nameMsg.textContent = '3\u201320 characters'; nameMsg.style.color = '#556677'; }
      else if (len >= 3) { nameIcon.textContent = '\u2713'; nameIcon.style.color = '#00ff88'; nameIcon.style.opacity = '1'; nameMsg.innerHTML = '<span style="color:#00ff88">\u2713</span> Name available (3\u201320 characters)'; nameMsg.style.color = '#00ff88'; }
      else { nameIcon.textContent = '\u2715'; nameIcon.style.color = '#ff6666'; nameIcon.style.opacity = '0.6'; nameMsg.textContent = 'Name too short'; nameMsg.style.color = '#ff6666'; }
      this.refreshCTAState(); this.updatePreview();
    });

    overlay.appendChild(mkSpacer(g(24)));

    // ════════════════════════════════════════════
    //  TAG (full width, same height as name)
    // ════════════════════════════════════════════
    overlay.appendChild(mkLabel('TAG', 'sc-tag-cnt', 3));
    overlay.appendChild(mkSpacer(g(10)));

    const tagWrap = document.createElement('div');
    tagWrap.style.cssText = 'position:relative;';
    this.tagInput = document.createElement('input');
    this.tagInput.type = 'text';
    this.tagInput.placeholder = 'Enter tag...';
    this.tagInput.maxLength = 3;
    this.tagInput.tabIndex = 2;
    this.tagInput.style.cssText = `${inputCss} width:100%;height:${g(40)}px;padding:0 ${180*sx}px 0 ${14*sx}px;font-size:${14*sy}px;font-weight:bold;text-transform:uppercase;letter-spacing:3px;color:#ffcc00;`;
    addFB(this.tagInput);

    const tagSugInline = document.createElement('span');
    tagSugInline.style.cssText = `position:absolute;right:${10*sx}px;top:50%;transform:translateY(-50%);font-size:${11*sy}px;color:#556677;opacity:0;transition:opacity .2s;pointer-events:auto;white-space:nowrap;`;
    tagWrap.appendChild(this.tagInput);
    tagWrap.appendChild(tagSugInline);
    overlay.appendChild(tagWrap);

    const tagMsg = document.createElement('div');
    tagMsg.style.cssText = `font-size:${12*sy}px;color:#556677;margin-top:${g(6)}px;min-height:${g(16)}px;`;
    tagMsg.textContent = '3 characters';
    overlay.appendChild(tagMsg);

    this.tagInput.addEventListener('input', () => {
      if (this.tagInput) this.tagInput.value = this.tagInput.value.replace(/[^A-Za-z0-9]/g, '').toUpperCase();
      const len = this.tagInput?.value.length || 0;
      const c = overlay.querySelector('#sc-tag-cnt');
      if (c) { c.textContent = `${len} / 3`; (c as HTMLElement).style.color = len === 3 ? '#00ff88' : 'rgba(68,85,102,0.8)'; }
      if (len === 3) {
        tagSugInline.style.opacity = '0';
        tagMsg.innerHTML = '<span style="color:#00ff88">\u2713</span> 3 characters';
        tagMsg.style.color = '#00ff88';
      } else if (len > 0) {
        tagSugInline.style.opacity = '0';
        tagMsg.textContent = 'Tag too short'; tagMsg.style.color = '#ff6666';
      } else {
        tagSugInline.style.opacity = '0';
        tagMsg.textContent = '3 characters'; tagMsg.style.color = '#556677';
      }
      this.refreshCTAState(); this.updatePreview();
    });

    overlay.appendChild(mkSpacer(g(28)));

    // ════════════════════════════════════════════
    //  DESCRIPTION (OPTIONAL)
    // ════════════════════════════════════════════
    overlay.appendChild(mkLabel('DESCRIPTION <span style="opacity:0.45;font-size:' + (11*sy) + 'px">(OPTIONAL)</span>', 'sc-desc-cnt', 200));
    overlay.appendChild(mkSpacer(g(10)));

    this.descInput = document.createElement('textarea');
    this.descInput.maxLength = 200;
    this.descInput.tabIndex = 3;
    this.descInput.value = '';
    this.descInput.style.cssText = `${inputCss} width:100%;height:${g(84)}px;padding:${g(10)}px ${14*sx}px;font-size:${13*sy}px;resize:none;line-height:1.5;`;
    addFB(this.descInput);
    this.descInput.addEventListener('input', () => {
      const c = overlay.querySelector('#sc-desc-cnt');
      const len = this.descInput?.value.length || 0;
      if (c) { c.textContent = `${len} / 200`; (c as HTMLElement).style.color = len>0 ? '#00ff88' : 'rgba(68,85,102,0.8)'; }
      this.updatePreview();
    });
    overlay.appendChild(this.descInput);
    // Fire initial counter update
    this.descInput.dispatchEvent(new Event('input'));

    overlay.appendChild(mkSpacer(g(16)));
    overlay.appendChild(mkDivider());

    // ════════════════════════════════════════════
    //  EMBLEM UPLOAD (large dropzone)
    // ════════════════════════════════════════════
    const dropzone = document.createElement('div');
    dropzone.style.cssText = `
      display:flex;align-items:center;gap:${14*sx}px;
      padding:${g(16)}px ${16*sx}px;
      background:rgba(10,21,37,0.5);border:1.5px dashed #1e3a5e;border-radius:10px;
      cursor:pointer;transition:all .2s;
    `;
    dropzone.addEventListener('mouseenter', () => { dropzone.style.borderColor='#2a5a8a'; dropzone.style.background='rgba(10,21,37,0.8)'; });
    dropzone.addEventListener('mouseleave', () => { dropzone.style.borderColor='#1e3a5e'; dropzone.style.background='rgba(10,21,37,0.5)'; });
    dropzone.addEventListener('dragover', (e) => { e.preventDefault(); dropzone.style.borderColor='#00bbcc'; dropzone.style.background='rgba(0,200,220,0.08)'; });
    dropzone.addEventListener('dragleave', () => { dropzone.style.borderColor='#1e3a5e'; dropzone.style.background='rgba(10,21,37,0.5)'; });
    dropzone.addEventListener('drop', (e) => {
      e.preventDefault(); dropzone.style.borderColor='#1e3a5e'; dropzone.style.background='rgba(10,21,37,0.5)';
      const f = e.dataTransfer?.files[0];
      if (f) this.handleEmblemFile(f, emblemThumb, uploadMain);
    });

    const emblemThumb = document.createElement('div');
    emblemThumb.style.cssText = `
      width:${50*sx}px;height:${50*sx}px;flex-shrink:0;
      background:#060d1a;border:2px solid #1e3a5e;border-radius:50%;
      display:flex;align-items:center;justify-content:center;
      font-size:${20*sx}px;color:#334455;overflow:hidden;transition:border-color .2s;
    `;
    emblemThumb.textContent = '+';
    dropzone.appendChild(emblemThumb);

    const uploadCol = document.createElement('div');
    uploadCol.style.cssText = `display:flex;flex-direction:column;gap:${g(4)}px;flex:1;`;
    const uploadMain = document.createElement('span');
    uploadMain.innerHTML = `Drop emblem here or <span style="color:#FDB15B;font-weight:bold">Browse</span>`;
    uploadMain.style.cssText = `font-family:${Fonts.heading};font-size:${13*sy}px;color:#8899bb;`;
    uploadCol.appendChild(uploadMain);

    const uploadRow = document.createElement('div');
    uploadRow.style.cssText = `display:flex;align-items:center;gap:${8*sx}px;`;
    const uploadFmt = document.createElement('span');
    uploadFmt.textContent = 'PNG or JPG \u00b7 64\u2013512px \u00b7 Max 256 KB';
    uploadFmt.style.cssText = `font-size:${11*sy}px;color:#445566;`;
    uploadRow.appendChild(uploadFmt);

    // Remove emblem button (hidden by default, shown after upload)
    this.removeBtn = document.createElement('span');
    this.removeBtn.textContent = '\u2715 Remove';
    this.removeBtn.style.cssText = `
      display:none;font-size:${11*sy}px;color:#ff6666;cursor:pointer;
      font-family:${Fonts.heading};letter-spacing:0.3px;
      transition:color .15s;
    `;
    this.removeBtn.addEventListener('mouseenter', () => { if (this.removeBtn) this.removeBtn.style.color = '#ff9999'; });
    this.removeBtn.addEventListener('mouseleave', () => { if (this.removeBtn) this.removeBtn.style.color = '#ff6666'; });
    this.removeBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      this.removeEmblem(emblemThumb, uploadMain);
    });
    uploadRow.appendChild(this.removeBtn);
    uploadCol.appendChild(uploadRow);
    dropzone.appendChild(uploadCol);

    this.emblemInput = document.createElement('input');
    this.emblemInput.type = 'file';
    this.emblemInput.accept = 'image/png,image/jpeg,image/gif,image/webp';
    this.emblemInput.style.display = 'none';
    overlay.appendChild(this.emblemInput);
    this.emblemInput.addEventListener('change', () => {
      const f = this.emblemInput?.files?.[0];
      if (f) {
        this.handleEmblemFile(f, emblemThumb, uploadMain);
        // Show the remove button after successful upload
        if (this.removeBtn) this.removeBtn.style.display = 'inline';
      }
    });
    dropzone.addEventListener('click', () => { this.emblemInput?.click(); });
    overlay.appendChild(dropzone);

    // Status area
    overlay.appendChild(mkSpacer(g(10)));
    this.statusEl = document.createElement('p');
    this.statusEl.style.cssText = `font-size:${12*sy}px;color:#ff6666;margin:0;min-height:${g(14)}px;`;
    overlay.appendChild(this.statusEl);

    // Enter submits
    overlay.addEventListener('keydown', (e: KeyboardEvent) => {
      if (e.key === 'Enter' && this.isFormValid()) { e.preventDefault(); this.submitCreateForm(); }
    });

    document.body.appendChild(overlay);

    // ════════════════════════════════════════════
    //  FOOTER (full-width HTML overlay at bottom)
    // ════════════════════════════════════════════
    const fLeft = canvasRect.left + (this.x + 24) * sx;
    const fTop = canvasRect.top + (this.y + this.dh - 62) * sy;
    const fW = (this.dw - 48) * sx;

    this.footerOverlay = document.createElement('div');
    this.footerOverlay.style.cssText = `
      position:fixed;left:${fLeft}px;top:${fTop}px;
      width:${fW}px;height:${56*sy}px;
      z-index:100001;pointer-events:auto;
      display:flex;align-items:center;justify-content:space-between;
      font-family:${Fonts.body};color:#fff;
    `;
    ['mousedown','mouseup','click','touchstart','touchend','keydown','keyup','wheel']
      .forEach(evt => this.footerOverlay!.addEventListener(evt, stop));

    const costStack = document.createElement('div');
    // Mobile: keep the cost-stack narrow so the create button has room
    // and the summary text doesn't push outside the dialog bounds.
    costStack.style.cssText = mobile
      ? `display:flex;flex-direction:column;gap:${g(2)}px;flex:0 1 auto;min-width:0;max-width:${Math.round(fW * 0.40)}px;overflow:hidden;`
      : `display:flex;flex-direction:column;gap:${g(4)}px;`;
    this.validationSummary = document.createElement('span');
    this.validationSummary.style.cssText = mobile
      ? `font-family:${Fonts.heading};font-size:${10*sy}px;color:#6888a8;letter-spacing:0.2px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;`
      : `font-family:${Fonts.heading};font-size:${12*sy}px;color:#6888a8;letter-spacing:0.3px;`;
    this.validationSummary.textContent = mobile
      ? 'You will be the leader'
      : 'You become alliance leader on creation';
    costStack.appendChild(this.validationSummary);
    const costLine = document.createElement('span');
    const costFontPx = mobile ? 10 : 12;
    const costValueFontPx = mobile ? 11 : 13;
    costLine.innerHTML = `<span style="font-family:${Fonts.heading};font-size:${costFontPx*sy}px;color:#6888a8;letter-spacing:0.5px;white-space:nowrap">COST: <span style="color:#00ff88;font-size:${costValueFontPx*sy}px">FREE</span></span>`;
    costStack.appendChild(costLine);
    this.footerOverlay.appendChild(costStack);

    this.createBtn = document.createElement('button');
    this.createBtn.textContent = 'CREATE ALLIANCE (FREE)';
    this.createBtn.tabIndex = 5;
    this.createBtn.disabled = true;
    // Mobile: button takes the right ~55% of the footer (cost-stack on left).
    // Desktop: button matches the preview column width on the right.
    const btnWidthCss = mobile ? `${Math.round(fW * 0.55)}px` : `${PREVIEW_W * sx}px`;
    this.createBtn.style.cssText = `
      width:${btnWidthCss};height:${g(46)}px;flex-shrink:0;
      background:linear-gradient(135deg,rgba(0,200,220,0.15),rgba(0,255,255,0.08));
      color:#00ffff;border:2px solid rgba(0,255,255,0.4);border-radius:8px;
      font-family:${Fonts.heading};font-size:${14*sy}px;letter-spacing:1.5px;
      cursor:not-allowed;opacity:0.35;transition:all .2s;
    `;
    this.createBtn.addEventListener('mouseenter', () => {
      if (this.createBtn?.disabled) return;
      this.createBtn!.style.background='linear-gradient(135deg,rgba(0,200,220,0.3),rgba(0,255,255,0.18))';
      this.createBtn!.style.borderColor='#00ffff'; this.createBtn!.style.color='#ffffff';
      this.createBtn!.style.boxShadow='0 0 24px rgba(0,255,255,0.25)';
    });
    this.createBtn.addEventListener('mouseleave', () => {
      if (this.createBtn?.disabled) return;
      this.createBtn!.style.background='linear-gradient(135deg,rgba(0,200,220,0.15),rgba(0,255,255,0.08))';
      this.createBtn!.style.borderColor='rgba(0,255,255,0.4)'; this.createBtn!.style.color='#00ffff';
      this.createBtn!.style.boxShadow='none';
    });
    this.createBtn.addEventListener('click', () => this.submitCreateForm());
    this.footerOverlay.appendChild(this.createBtn);
    document.body.appendChild(this.footerOverlay);

    // Focus name input
    this.scene.time.delayedCall(100, () => { this.nameInput?.focus(); });

    // ════════════════════════════════════════════
    //  PREVIEW (right column — Phaser, desktop only)
    // ════════════════════════════════════════════
    if (!mobile) {
      this.previewTabBar = this.scene.add.container(PREVIEW_X, 25);
      this.contentContainer.add(this.previewTabBar);
      this.buildPreviewTabs(PREVIEW_W);

      this.previewContainer = this.scene.add.container(PREVIEW_X, 59);
      this.contentContainer.add(this.previewContainer);
    }

    this.refreshCTAState();
    this.updatePreview();
  }

  /** Draw the preview tab bar (MAP EMBLEM / CHAT EMBLEM / LIST CARD) */
  private buildPreviewTabs(pw?: number): void {
    if (!this.previewTabBar) return;
    this.previewTabBar.removeAll(true);
    const PW = pw || (this.dw - 40 - 380 - 36);
    const tabs: { id: 'map' | 'chat' | 'list'; label: string }[] = [
      { id: 'map', label: 'ALLIANCE' },
      { id: 'chat', label: 'CHAT' },
      { id: 'list', label: 'LIST' },
    ];
    const tw = PW / 3;
    tabs.forEach((tab, i) => {
      const active = tab.id === this.previewTab;
      const bg = this.scene.add.graphics();
      bg.fillStyle(active ? 0x0c2a3a : 0x080e1a, active ? 0.95 : 0.5);
      bg.fillRoundedRect(i * tw + 2, 0, tw - 4, 28, { tl: 6, tr: 6, bl: 0, br: 0 });
      if (active) { bg.lineStyle(1, Colors.cyan, 0.4); bg.strokeRoundedRect(i * tw + 2, 0, tw - 4, 28, { tl: 6, tr: 6, bl: 0, br: 0 }); }
      this.previewTabBar!.add(bg);
      const txt = this.scene.add.text(i * tw + tw / 2, 14, tab.label, {
        fontSize: '10px', color: active ? CSSColors.cyan : CSSColors.textDim, fontFamily: Fonts.heading,
      });
      txt.setOrigin(0.5);
      this.previewTabBar!.add(txt);
      const zone = this.scene.add.zone(i * tw + tw / 2, 14, tw, 28).setInteractive({ useHandCursor: true });
      zone.on('pointerdown', () => { this.previewTab = tab.id; this.buildPreviewTabs(PW); this.updatePreview(); });
      this.previewTabBar!.add(zone);
    });
  }

  /** Handle an emblem file from either browse or drag-and-drop */
  private handleEmblemFile(file: File, thumbEl: HTMLDivElement, labelEl: HTMLSpanElement): void {
    const MAX_SIZE = 256 * 1024;       // 256 KB
    const MAX_DIM = 512;               // 512x512 max
    const MIN_DIM = 64;                // 64x64 min

    if (file.size > MAX_SIZE) {
      if (this.statusEl) { this.statusEl.textContent = 'Image too large (max 256 KB).'; this.statusEl.style.color = '#ff6666'; }
      return;
    }
    if (!file.type.startsWith('image/')) {
      if (this.statusEl) { this.statusEl.textContent = 'File must be an image (PNG, JPG, GIF, WebP).'; this.statusEl.style.color = '#ff6666'; }
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result as string;

      // Validate dimensions before accepting
      const checkImg = new Image();
      checkImg.onload = () => {
        const w = checkImg.naturalWidth;
        const h = checkImg.naturalHeight;
        if (w < MIN_DIM || h < MIN_DIM) {
          if (this.statusEl) { this.statusEl.textContent = `Image too small (${w}\u00d7${h}). Minimum ${MIN_DIM}\u00d7${MIN_DIM}px.`; this.statusEl.style.color = '#ff6666'; }
          return;
        }
        if (w > MAX_DIM || h > MAX_DIM) {
          if (this.statusEl) { this.statusEl.textContent = `Image too large (${w}\u00d7${h}). Maximum ${MAX_DIM}\u00d7${MAX_DIM}px.`; this.statusEl.style.color = '#ff6666'; }
          return;
        }

        // Dimensions OK — apply the emblem
        this.emblemDataUrl = dataUrl;
        thumbEl.innerHTML = '';
        thumbEl.style.borderColor = '#00ff88';
        const img = document.createElement('img');
        img.src = this.emblemDataUrl;
        img.style.cssText = 'width:100%;height:100%;object-fit:cover;border-radius:50%;';
        thumbEl.appendChild(img);
        labelEl.textContent = 'Emblem uploaded \u2713';
        labelEl.style.color = '#00ff88';
        if (this.statusEl) this.statusEl.textContent = '';

        // Show the remove button
        if (this.removeBtn) this.removeBtn.style.display = 'inline';

        // Load the uploaded image as a Phaser texture for the preview
        if (this.scene.textures.exists('custom-emblem')) {
          this.scene.textures.remove('custom-emblem');
        }
        const texImg = new Image();
        texImg.onload = () => {
          if (this.scene && this.scene.textures) {
            this.scene.textures.addImage('custom-emblem', texImg);
            this.updatePreview();
          }
        };
        texImg.src = this.emblemDataUrl;
      };
      checkImg.src = dataUrl;
    };
    reader.readAsDataURL(file);
  }

  /** Remove the uploaded emblem and revert to default */
  private removeEmblem(thumbEl: HTMLDivElement, labelEl: HTMLSpanElement): void {
    this.emblemDataUrl = undefined;
    if (this.scene.textures.exists('custom-emblem')) {
      this.scene.textures.remove('custom-emblem');
    }
    thumbEl.innerHTML = '';
    thumbEl.style.borderColor = '#1e3a5e';
    thumbEl.textContent = '+';
    labelEl.innerHTML = `Drop emblem here or <span style="color:#FDB15B;font-weight:bold">Browse</span>`;
    labelEl.style.color = '#8899bb';
    // Hide the remove button
    if (this.removeBtn) { this.removeBtn.style.display = 'none'; }
    if (this.statusEl) this.statusEl.textContent = '';
    this.updatePreview();
  }

  /** Submit the create alliance form */
  private submitCreateForm(): void {
    if (!this.isFormValid()) return;
    const name = this.nameInput?.value.trim() || '';
    const tag = this.tagInput?.value.trim().toUpperCase() || '';
    const desc = this.descInput?.value.trim() || '';
    if (this.createBtn) {
      this.createBtn.textContent = 'CREATING...';
      this.createBtn.disabled = true;
      this.createBtn.style.opacity = '0.5';
      this.createBtn.style.cursor = 'wait';
    }
    if (this.statusEl) { this.statusEl.textContent = 'Creating alliance...'; this.statusEl.style.color = '#88ddff'; }
    this.mmoNetwork.sendAllianceCreate(name, tag, desc || undefined);
  }

  /** Rebuild the live alliance emblem preview */
  private updatePreview(): void {
    if (!this.previewContainer) return;
    const pc = this.previewContainer;
    pc.removeAll(true);
    const PW = this.dw - 40 - 380 - 36;
    const name = this.nameInput?.value.trim() || 'Alliance Name';
    const tag = (this.tagInput?.value.trim().toUpperCase()) || 'TAG';
    const desc = this.descInput?.value.trim() || '';

    if (this.previewTab === 'map') {
      this.drawMapBadgePreview(pc, PW, name, tag, desc);
    } else if (this.previewTab === 'chat') {
      this.drawChatBadgePreview(pc, PW, name, tag);
    } else {
      this.drawListCardPreview(pc, PW, name, tag, desc);
    }
  }

  private drawMapBadgePreview(pc: Phaser.GameObjects.Container, pw: number, name: string, tag: string, desc: string): void {
    let y = 0;
    const EMBLEM_SIZE = 165;
    const EMBLEM_H = EMBLEM_SIZE + 110;
    const bg = this.scene.add.graphics();
    bg.fillStyle(Colors.bgCard, 0.5);
    bg.fillRoundedRect(0, y, pw, EMBLEM_H, 10);
    bg.lineStyle(1, Colors.borderDim, 0.3);
    bg.strokeRoundedRect(0, y, pw, EMBLEM_H, 10);
    pc.add(bg);

    const CX = Math.round(pw / 2);
    const CY = y + Math.round(EMBLEM_SIZE / 2) + 12;

    // Emblem image — show uploaded custom emblem or default badge
    const emblemKey = this.emblemDataUrl && this.scene.textures.exists('custom-emblem') ? 'custom-emblem' : 'alliance-badge';
    if (this.scene.textures.exists(emblemKey)) {
      const emblemImg = this.scene.add.image(CX, CY, emblemKey);
      emblemImg.setDisplaySize(EMBLEM_SIZE, EMBLEM_SIZE);
      emblemImg.setOrigin(0.5, 0.5);
      pc.add(emblemImg);
    }

    // Name + [TAG] on the same line below emblem (always show both)
    const nameY = CY + Math.round(EMBLEM_SIZE / 2) + 14;
    const displayName = name.substring(0, 20);
    const displayTag = tag.substring(0, 3) || 'TAG';
    const tagLabel = ` [${displayTag}]`;

    // Measure both texts to center them together
    const tmpName = this.scene.add.text(0, 0, displayName, {
      fontSize: FontSize.lg, color: '#e0e8ff', fontFamily: Fonts.heading,
    });
    const nameW = tmpName.width;
    tmpName.destroy();
    const tmpTag = this.scene.add.text(0, 0, tagLabel, {
      fontSize: FontSize.md, color: CSSColors.gold, fontFamily: Fonts.heading, fontStyle: 'bold',
    });
    const tagW = tmpTag.width;
    tmpTag.destroy();

    const totalW = nameW + tagW;
    const startX = CX - totalW / 2;

    const nt = this.scene.add.text(startX, nameY, displayName, {
      fontSize: FontSize.lg, color: '#e0e8ff', fontFamily: Fonts.heading,
    });
    nt.setOrigin(0, 0); pc.add(nt);

    const tagText = this.scene.add.text(startX + nameW, nameY + 1, tagLabel, {
      fontSize: FontSize.md, color: CSSColors.gold, fontFamily: Fonts.heading,
      fontStyle: 'bold',
    });
    tagText.setOrigin(0, 0);
    pc.add(tagText);

    // Description
    const descY = nameY + 24;
    if (desc) {
      const dp = this.scene.add.text(CX, descY, desc.length > 60 ? desc.substring(0, 57) + '...' : desc, {
        fontSize: FontSize.xs, color: CSSColors.textMuted, fontFamily: Fonts.body, align: 'center', wordWrap: { width: pw - 20 },
      });
      dp.setOrigin(0.5, 0); pc.add(dp);
    }
    y += EMBLEM_H + 12;

    // Divider
    const dv = this.scene.add.graphics();
    dv.lineStyle(1, Colors.borderDim, 0.3);
    dv.lineBetween(8, y, pw - 8, y);
    pc.add(dv); y += 10;

    // Benefits (all shown)
    pc.add(this.scene.add.text(0, y, 'ALLIANCE BENEFITS', { fontSize: FontSize.xs, color: CSSColors.textCyan, fontFamily: Fonts.heading }));
    y += 20;
    const benefits = ['Shared vision & fog of war', 'Alliance chat & coordination', 'Alliance tax & treasury', 'Alliance HQ & territory', 'Station raids', 'Territory bonuses', 'Starts with 10 slots \u2014 grow with stations'];
    benefits.forEach(b => {
      pc.add(this.scene.add.text(4, y, '\u203A', { fontSize: FontSize.xs, color: CSSColors.cyan, fontFamily: Fonts.heading }));
      pc.add(this.scene.add.text(16, y, b, { fontSize: FontSize.xs, color: CSSColors.textLight, fontFamily: Fonts.body }));
      y += 17;
    });
  }

  private drawChatBadgePreview(pc: Phaser.GameObjects.Container, pw: number, _name: string, tag: string): void {
    let y = 12;
    // Title
    pc.add(this.scene.add.text(pw / 2, y, 'Chat Preview', { fontSize: FontSize.xs, color: CSSColors.textDim, fontFamily: Fonts.heading }).setOrigin(0.5, 0));
    y += 28;

    // Simulated chat messages — chat shows [TAG] CommanderName, not alliance name
    const msgs = [
      { user: 'StarPilot', msg: 'Anyone up for a raid?', ally: false },
      { user: 'NovaX', msg: 'Count me in!', ally: false },
      { user: 'You', msg: 'Rally at sector 7', ally: true },
    ];
    msgs.forEach(m => {
      if (m.ally) {
        const hlBg = this.scene.add.graphics();
        hlBg.fillStyle(Colors.cyan, 0.04);
        hlBg.fillRoundedRect(0, y - 4, pw, 38, 4);
        pc.add(hlBg);
      }
      const prefix = m.ally ? `[${tag}] ` : '';
      const ut = this.scene.add.text(10, y, prefix + m.user, {
        fontSize: FontSize.xs, color: m.ally ? CSSColors.gold : CSSColors.textLight, fontFamily: Fonts.heading,
      });
      pc.add(ut);
      const mt = this.scene.add.text(10, y + 16, m.msg, {
        fontSize: FontSize.xs, color: CSSColors.textMuted, fontFamily: Fonts.body, wordWrap: { width: pw - 20 },
      });
      pc.add(mt);
      y += 42;
    });
    y += 16;
    const note = this.scene.add.text(pw / 2, y, 'Your tag appears next to messages', {
      fontSize: '10px', color: CSSColors.textDim, fontFamily: Fonts.body,
    });
    note.setOrigin(0.5, 0); pc.add(note);
  }

  private drawListCardPreview(pc: Phaser.GameObjects.Container, pw: number, name: string, tag: string, desc: string): void {
    let y = 12;
    pc.add(this.scene.add.text(pw / 2, y, 'Alliance Browser', { fontSize: FontSize.xs, color: CSSColors.textDim, fontFamily: Fonts.heading }).setOrigin(0.5, 0));
    y += 28;

    // Card
    const cardH = 100;
    const cardBg = this.scene.add.graphics();
    cardBg.fillStyle(Colors.bgCard, 0.7);
    cardBg.fillRoundedRect(0, y, pw, cardH, 8);
    cardBg.lineStyle(1, Colors.borderMed, 0.4);
    cardBg.strokeRoundedRect(0, y, pw, cardH, 8);
    pc.add(cardBg);

    // Small emblem circle — show custom emblem if uploaded
    const ecx = 36, ecy = y + cardH / 2;
    const listEmblemKey = this.emblemDataUrl && this.scene.textures.exists('custom-emblem') ? 'custom-emblem' : null;
    if (listEmblemKey) {
      // Circular mask for custom emblem
      const emblemSmall = this.scene.add.image(ecx, ecy, listEmblemKey);
      emblemSmall.setDisplaySize(40, 40);
      emblemSmall.setOrigin(0.5, 0.5);
      const maskGfx = this.scene.add.graphics();
      maskGfx.fillStyle(0xffffff);
      maskGfx.fillCircle(ecx + pc.x + this.x, ecy + pc.y + this.y, 20);
      emblemSmall.setMask(maskGfx.createGeometryMask());
      pc.add(emblemSmall);
      // Border ring
      const ring = this.scene.add.graphics();
      ring.lineStyle(2, Colors.cyan, 0.5);
      ring.strokeCircle(ecx, ecy, 20);
      pc.add(ring);
    } else if (this.scene.textures.exists('alliance-badge')) {
      // Show default alliance emblem scaled down
      const defaultSmall = this.scene.add.image(ecx, ecy, 'alliance-badge');
      defaultSmall.setDisplaySize(40, 40);
      defaultSmall.setOrigin(0.5, 0.5);
      pc.add(defaultSmall);
    } else {
      // Fallback circle with tag initials
      const eg = this.scene.add.graphics();
      eg.fillStyle(Colors.bgElevated, 0.9);
      eg.fillCircle(ecx, ecy, 20);
      eg.lineStyle(2, Colors.cyan, 0.5);
      eg.strokeCircle(ecx, ecy, 20);
      pc.add(eg);
      const dt = tag !== 'TAG' ? tag.substring(0, 3) : '';
      if (dt) pc.add(this.scene.add.text(ecx, ecy, dt, { fontSize: FontSize.sm, color: CSSColors.gold, fontFamily: Fonts.heading }).setOrigin(0.5));
    }

    // Name + tag
    pc.add(this.scene.add.text(66, y + 14, name.substring(0, 20), { fontSize: FontSize.md, color: '#e0e8ff', fontFamily: Fonts.heading }));
    pc.add(this.scene.add.text(66, y + 36, `[${tag}]`, { fontSize: FontSize.xs, color: CSSColors.gold, fontFamily: Fonts.heading }));
    // Description
    const dText = desc || 'No description';
    pc.add(this.scene.add.text(66, y + 56, dText.length > 40 ? dText.substring(0, 37) + '...' : dText, {
      fontSize: FontSize.xs, color: CSSColors.textMuted, fontFamily: Fonts.body, wordWrap: { width: pw - 80 },
    }));
    // Members
    pc.add(this.scene.add.text(pw - 10, y + 14, '1 / 100', { fontSize: FontSize.xs, color: CSSColors.textDim, fontFamily: Fonts.body }).setOrigin(1, 0));
    pc.add(this.scene.add.text(pw - 10, y + 30, 'members', { fontSize: '10px', color: CSSColors.textDim, fontFamily: Fonts.body }).setOrigin(1, 0));

    y += cardH + 16;
    const note = this.scene.add.text(pw / 2, y, 'How your alliance appears to others', {
      fontSize: '10px', color: CSSColors.textDim, fontFamily: Fonts.body,
    });
    note.setOrigin(0.5, 0); pc.add(note);
  }

  private onAllianceDetails(data: any): void {
    if (!data) return;
    console.log('[Alliance] Received alliance details:', data);

    // Update alliance data with real server data
    if (data.name) this.allianceData.name = data.name;
    if (data.tag) this.allianceData.tag = data.tag;
    if (data.leaderId) this.allianceData.leaderId = data.leaderId;
    if (data.description !== undefined) this.allianceData.description = data.description;

    // Update members with real data
    if (data.members && Array.isArray(data.members)) {
      this.allianceData.members = data.members.map((m: any) => ({
        userId: m.userId,
        username: m.username || 'Unknown',
        rank: m.rank || 'member',
        online: !!m.online,
        lastSeenDaysAgo: 0, // Server doesn't track this yet
        ships: m.ships || 0,
        stations: m.stations || 0,
        crew: 0, // Not tracked
        joinedAt: m.joinedAt ? new Date(m.joinedAt).getTime() : Date.now(),
      }));
    }

    // Store recent activity
    if (data.recentActivity) {
      this.allianceData.recentActivity = data.recentActivity;
    }

    // Re-render the view with real data
    if (this.hasAlliance) {
      this.contentContainer.removeAll(true);
      this.showAllianceView();
    }
  }

  private onAllianceCreated(data: any): void {
    console.log('[Alliance] Alliance created successfully:', data);

    // ── Success animation ──
    this.contentContainer.removeAll(true);
    this.cleanupHTMLInputs();
    if (this.previewContainer) {
      this.previewContainer.removeAll(true);
      this.previewContainer = undefined;
    }

    const cx = (this.dw - 40) / 2;
    const cy = 180;

    // Success ring burst
    const ring = this.scene.add.graphics();
    ring.lineStyle(3, Colors.cyan, 1);
    ring.strokeCircle(cx, cy, 10);
    this.contentContainer.add(ring);

    this.scene.tweens.add({
      targets: ring,
      scaleX: 4,
      scaleY: 4,
      alpha: 0,
      duration: 600,
      ease: 'Quad.easeOut',
    });

    // Checkmark
    const check = this.scene.add.text(cx, cy, '✓', {
      fontSize: '48px',
      color: CSSColors.green,
      fontFamily: Fonts.heading,
    });
    check.setOrigin(0.5);
    check.setAlpha(0);
    check.setScale(0.3);
    this.contentContainer.add(check);

    this.scene.tweens.add({
      targets: check,
      alpha: 1,
      scaleX: 1,
      scaleY: 1,
      duration: 400,
      delay: 200,
      ease: 'Back.easeOut',
    });

    // Title text
    const successText = this.scene.add.text(cx, cy + 60, 'ALLIANCE CREATED', {
      fontSize: FontSize.xl,
      color: '#ffffff',
      fontFamily: Fonts.heading,
    });
    successText.setOrigin(0.5);
    successText.setAlpha(0);
    this.contentContainer.add(successText);

    this.scene.tweens.add({
      targets: successText,
      alpha: 1,
      y: cy + 50,
      duration: 400,
      delay: 400,
      ease: 'Quad.easeOut',
    });

    // Sub-message
    const subText = this.scene.add.text(cx, cy + 82, 'Rally your commanders!', {
      fontSize: FontSize.md,
      color: CSSColors.textMuted,
      fontFamily: Fonts.body,
    });
    subText.setOrigin(0.5);
    subText.setAlpha(0);
    this.contentContainer.add(subText);

    this.scene.tweens.add({
      targets: subText,
      alpha: 1,
      y: cy + 78,
      duration: 400,
      delay: 600,
      ease: 'Quad.easeOut',
    });

    // Auto-close after 2s
    this.scene.time.delayedCall(2000, () => {
      this.close();
    });
  }

  /** Remove all HTML overlay inputs from the DOM */
  private cleanupHTMLInputs(): void {
    if (this.escHandler) {
      document.removeEventListener('keydown', this.escHandler);
      this.escHandler = undefined;
    }
    if (this.formOverlay) { this.formOverlay.remove(); this.formOverlay = undefined; }
    if (this.footerOverlay) { this.footerOverlay.remove(); this.footerOverlay = undefined; }
    if (this.heroContainer) { this.heroContainer.destroy(); this.heroContainer = undefined; }
    if (this.previewTabBar) { this.previewTabBar.destroy(); this.previewTabBar = undefined; }
    this.nameInput = undefined;
    this.tagInput = undefined;
    this.descInput = undefined;
    this.emblemInput = undefined;
    this.removeBtn = undefined;
    this.emblemDataUrl = undefined;
    if (this.scene.textures.exists('custom-emblem')) {
      this.scene.textures.remove('custom-emblem');
    }
    this.createBtn = undefined;
    this.statusEl = undefined;
    this.validationSummary = undefined;
    this.selectedChips.clear();
    this.previewTab = 'map';

    if (this.scene.input.keyboard) {
      this.scene.input.keyboard.enabled = true;
    }
    EventBus.emit('chat-input-unfocused');
  }

  private close(): void {
    this.cleanupHTMLInputs();
    this.cleanupChatOverlay();
    this.hideConfirm();
    // Remove event listeners
    EventBus.off(MMOEvents.ALLIANCE_CREATED, this.onAllianceCreated, this);
    EventBus.off(MMOEvents.ALLIANCE_DETAILS, this.onAllianceDetails, this);
    closeDialog(this.scene, this);
  }
}
